// src/components/home/CreatePostModal.tsx
import React from 'react';
import { FaTimes } from 'react-icons/fa';
import { ImageUpload } from '../ImageUpload';
 
interface CreatePostModalProps {
  darkMode: boolean;
  isOpen: boolean;
  postContent: string;
  postMediaUrl: string;
  isSubmitting: boolean;
  onContentChange: (content: string) => void;
  onMediaUpload: (url: string, type: 'image' | 'video') => void;
  onMediaRemove: () => void;
  onSubmit: () => void;
  onClose: () => void;
}
 
export const CreatePostModal: React.FC<CreatePostModalProps> = ({
  darkMode, isOpen, postContent, postMediaUrl,
  isSubmitting, onContentChange, onMediaUpload,
  onMediaRemove, onSubmit, onClose,
}) => {
  if (!isOpen) return null;
 
  const bg = darkMode ? '#0f2424' : '#f0fafa';
  const border = darkMode ? 'rgba(90,184,184,0.15)' : 'rgba(13,26,26,0.12)';
 
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(13,26,26,0.75)', backdropFilter: 'blur(8px)' }}>
      <div
        className="w-full max-w-lg p-6"
        style={{
          background: bg,
          border: `2px solid ${darkMode ? 'rgba(90,184,184,0.2)' : 'rgba(13,26,26,0.15)'}`,
          borderRadius: '16px',
          boxShadow: '6px 6px 0 rgba(13,26,26,0.3)',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <h2
            className="font-black text-base"
            style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '3px', color: darkMode ? '#f0fafa' : '#0d1a1a' }}
          >
            CREATE POST
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg transition-all"
            style={{ color: darkMode ? 'rgba(126,206,206,0.4)' : 'rgba(13,26,26,0.4)' }}
          >
            <FaTimes style={{ fontSize: '15px' }} />
          </button>
        </div>
 
        {/* Textarea */}
        <textarea
          value={postContent}
          onChange={e => onContentChange(e.target.value)}
          placeholder="Share something with the network..."
          rows={4}
          className="w-full p-4 rounded-xl resize-none text-sm focus:outline-none transition-all font-medium"
          style={{
            background: darkMode ? '#0d1a1a' : '#ffffff',
            border: `1.5px solid ${border}`,
            color: darkMode ? '#f0fafa' : '#0d1a1a',
          }}
          onFocus={e => (e.target.style.borderColor = '#5ab8b8')}
          onBlur={e => (e.target.style.borderColor = border)}
        />
 
        <div className="mt-4">
          <ImageUpload
            onUploadComplete={onMediaUpload}
            onRemove={onMediaRemove}
            currentPreview={postMediaUrl}
            darkMode={darkMode}
          />
        </div>
 
        {/* Submit */}
        <div className="flex justify-end mt-5">
          <button
            onClick={onSubmit}
            disabled={isSubmitting || !postContent.trim()}
            className="px-6 py-2.5 rounded-lg font-black text-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            style={{
              background: '#e8a820',
              border: '2.5px solid #0d1a1a',
              boxShadow: '3px 3px 0 #0d1a1a, 4px 4px 0 #8a5a00',
              color: '#0d1a1a',
              letterSpacing: '1px',
            }}
            onMouseEnter={e => { if (postContent.trim() && !isSubmitting) { (e.currentTarget as HTMLButtonElement).style.transform = 'translate(2px,2px)'; (e.currentTarget as HTMLButtonElement).style.boxShadow = '1px 1px 0 #0d1a1a'; } }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.transform = ''; (e.currentTarget as HTMLButtonElement).style.boxShadow = '3px 3px 0 #0d1a1a, 4px 4px 0 #8a5a00'; }}
          >
            {isSubmitting ? (
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 rounded-full border-2 border-t-transparent animate-spin"
                  style={{ borderColor: '#0d1a1a', borderTopColor: 'transparent' }} />
                POSTING...
              </span>
            ) : 'POST IT'}
          </button>
        </div>
      </div>
    </div>
  );
};